import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Building2, X, Loader2, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

/**
 * EmpresaSearchSelect - A searchable select component for companies
 * Replaces dropdown selects with a search-as-you-type interface
 */
const EmpresaSearchSelect = ({
  empresas = [],
  value,
  onChange,
  placeholder = "Buscar empresa...",
  disabled = false,
  allowClear = true,
  showNone = false,
  noneLabel = "Sin empresa",
  className = "",
  loading = false,
  maxResults = 20,
  'data-testid': dataTestId = "empresa-search-select"
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  
  // Find selected empresa
  const selectedEmpresa = value && value !== 'none' 
    ? empresas.find(e => e.id === value) 
    : null;
  
  // Filter empresas based on search term
  const filteredEmpresas = useCallback(() => {
    if (!searchTerm.trim()) {
      return empresas.slice(0, maxResults);
    }
    
    const term = searchTerm.toLowerCase().trim();
    return empresas
      .filter(empresa => {
        const name = (empresa.name || '').toLowerCase();
        const email = (empresa.email || '').toLowerCase();
        const phone = (empresa.phone || '').toLowerCase();
        const industry = (empresa.industry || '').toLowerCase();
        
        return name.includes(term) || 
               email.includes(term) || 
               phone.includes(term) ||
               industry.includes(term);
      })
      .slice(0, maxResults);
  }, [empresas, searchTerm, maxResults]);
  
  const results = filteredEmpresas();
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }
    
    const totalItems = showNone ? results.length + 1 : results.length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Enter':
        e.preventDefault();
        if (showNone && highlightedIndex === 0) {
          handleSelect('none');
        } else {
          const adjustedIndex = showNone ? highlightedIndex - 1 : highlightedIndex;
          if (results[adjustedIndex]) {
            handleSelect(results[adjustedIndex].id);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };
  
  const handleSelect = (empresaId) => {
    onChange(empresaId);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(0);
  };
  
  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm('');
  };
  
  const handleInputFocus = () => {
    setIsOpen(true);
  };
  
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedIndex(0);
    if (!isOpen) setIsOpen(true);
  };
  
  return (
    <div 
      ref={containerRef} 
      className={`relative ${className}`}
      data-testid={dataTestId}
    >
      {/* Selected Value Display / Search Input */}
      <div 
        className={`flex items-center border rounded-md bg-white transition-colors ${
          isOpen ? 'border-amber-500 ring-1 ring-amber-500' : 'border-gray-300 hover:border-gray-400'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-text'}`}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        <div className="flex items-center flex-1 px-3 py-2 gap-2">
          {loading ? (
            <Loader2 size={16} className="text-gray-400 animate-spin" />
          ) : (
            <Search size={16} className="text-gray-400 flex-shrink-0" />
          )}
          
          {selectedEmpresa && !isOpen ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Building2 size={14} className="text-amber-600 flex-shrink-0" />
              <span className="truncate text-sm font-medium">{selectedEmpresa.name}</span>
              {selectedEmpresa.industry && (
                <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                  {selectedEmpresa.industry}
                </Badge>
              )}
            </div>
          ) : value === 'none' && !isOpen ? (
            <span className="text-sm text-gray-500">{noneLabel}</span>
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              className="flex-1 outline-none text-sm bg-transparent min-w-0"
              data-testid={`${dataTestId}-input`}
            />
          )}
        </div>
        
        <div className="flex items-center pr-2 gap-1">
          {allowClear && (selectedEmpresa || value === 'none') && !disabled && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              type="button"
              data-testid={`${dataTestId}-clear`}
            >
              <X size={14} className="text-gray-400" />
            </button>
          )}
          <ChevronDown 
            size={16} 
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </div>
      
      {/* Dropdown Results */}
      {isOpen && !disabled && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${dataTestId}-dropdown`}
        >
          {/* None option */}
          {showNone && (
            <div
              className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
                highlightedIndex === 0 ? 'bg-amber-50' : 'hover:bg-gray-50'
              } ${value === 'none' ? 'bg-amber-50' : ''}`}
              onClick={() => handleSelect('none')}
              data-testid={`${dataTestId}-option-none`}
            >
              <span className="text-sm text-gray-500">{noneLabel}</span>
            </div>
          )}
          
          {/* Results */}
          {results.length > 0 ? (
            results.map((empresa, index) => {
              const adjustedIndex = showNone ? index + 1 : index;
              const isHighlighted = highlightedIndex === adjustedIndex;
              const isSelected = value === empresa.id;
              
              return (
                <div
                  key={empresa.id}
                  className={`px-3 py-2 cursor-pointer transition-colors ${
                    isHighlighted ? 'bg-amber-50' : 'hover:bg-gray-50'
                  } ${isSelected ? 'bg-amber-50 border-l-2 border-amber-500' : ''}`}
                  onClick={() => handleSelect(empresa.id)}
                  data-testid={`${dataTestId}-option-${empresa.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{empresa.name}</span>
                        {empresa.stage && (
                          <Badge variant="outline" className="text-xs">
                            {empresa.stage}
                          </Badge>
                        )}
                      </div>
                      {(empresa.email || empresa.industry) && (
                        <div className="text-xs text-gray-500 truncate">
                          {empresa.email && <span>{empresa.email}</span>}
                          {empresa.email && empresa.industry && <span> • </span>}
                          {empresa.industry && <span>{empresa.industry}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-3 py-4 text-center text-sm text-gray-500">
              {searchTerm ? (
                <>No se encontraron empresas para &quot;{searchTerm}&quot;</>
              ) : (
                <>No hay empresas disponibles</>
              )}
            </div>
          )}
          
          {/* Show count if there are more results */}
          {empresas.length > maxResults && !searchTerm && (
            <div className="px-3 py-2 text-xs text-gray-400 text-center border-t">
              Mostrando {maxResults} de {empresas.length} empresas. Escribe para buscar más.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmpresaSearchSelect;
